/* eslint-disable prettier/prettier */
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useState } from "react";

// ✅ TYPE (like IAddInvestor)
interface IDeliveryForm {
  _rowId?: string;
  deliveryType: number;
  path?: string;
  url?: string;
  port?: number;
  userName?: string;
  password?: string;
  privateKeyFile?: string;
  passPhrase?: string;
  subFolderName?: string;
}

// ✅ SCHEMA
const deliverySchema = Yup.object().shape({
  deliveryType: Yup.number().required(),

  path: Yup.string().when("deliveryType", {
    is: 1,
    then: (schema) => schema.required("Path is required"),
  }),

  url: Yup.string().when("deliveryType", {
    is: 2,
    then: (schema) => schema.required("URL is required"),
  }),

  port: Yup.number()
    .typeError("Port must be a number")
    .when("deliveryType", {
      is: 2,
      then: (schema) => schema.required("Port is required"),
    }),

  userName: Yup.string().when("deliveryType", {
    is: 2,
    then: (schema) => schema.required("Username is required"),
  }),

  password: Yup.string(),

  privateKeyFile: Yup.string(),

  passPhrase: Yup.string(),

  subFolderName: Yup.string().nullable(),
});

const DeliveryConfigForm = ({
  mode,
  data,
  packageData,
  onSave,
  onCancel,
}: any) => {
  const [showLoading, setShowLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IDeliveryForm>({
    resolver: yupResolver(deliverySchema),
    mode: "all",
    defaultValues: {
      deliveryType: 1,
      path: "",
      url: "",
      port: undefined,
      userName: "",
      password: "",
      privateKeyFile: "",
      passPhrase: "",
      subFolderName: "",
      ...data,
    },
  });

  const deliveryType = watch("deliveryType");

  const buildDeliveryPayload = (form: IDeliveryForm, rowId?: string) => {
    if (form.deliveryType === 1) {
      return {
        ...(rowId ? { _rowId: rowId } : {}),
        deliveryType: 1,
        path: form.path?.trim() || "",
        subFolderName: form.subFolderName?.trim() || "",
      };
    }

    return {
      ...(rowId ? { _rowId: rowId } : {}),
      deliveryType: 2,
      url: form.url?.trim() || "",
      port: form.port,
      userName: form.userName?.trim() || "",
      password: form.password?.trim() || "",
      privateKeyFile: form.privateKeyFile?.trim() || "",
      passPhrase: form.passPhrase?.trim() || "",
      path: form.path?.trim() || "",
      subFolderName: form.subFolderName?.trim() || "",
    };
  };

  // ✅ Submit
  const onSubmit = async (form: IDeliveryForm) => {
    setShowLoading(true);

    let updatedDeliveries = [...packageData.deliveryOptions];

    if (mode === "edit") {
      updatedDeliveries = updatedDeliveries.map((d: any) =>
        d._rowId === form._rowId ? buildDeliveryPayload(form, d._rowId) : d,
      );
    } else {
      updatedDeliveries.push({
        ...buildDeliveryPayload(form),
        _rowId: `new-${Date.now()}`,
      });
    }

    try {
      await onSave(updatedDeliveries);
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <div>
      <h2 className="pb-3 text-xl font-medium">
        {mode === "edit" ? "Edit Delivery" : "Add Delivery"}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          {/* Delivery Type */}
          <div>
            <Dropdown
              value={deliveryType}
              options={[
                { label: "Folder Copy", value: 1 },
                { label: "SFTP", value: 2 },
              ]}
              onChange={(e) => {
                const type = e.value;

                setValue("deliveryType", type, { shouldValidate: true });

                if (type === 1) {
                  // Folder Copy - clear SFTP fields
                  setValue("url", "");
                  setValue("port", undefined);
                  setValue("userName", "");
                  setValue("password", "");
                  setValue("privateKeyFile", "");
                  setValue("passPhrase", "");
                } else {
                  // SFTP - clear Folder Copy fields
                  setValue("path", "");
                }
              }}
              placeholder="Select Delivery Type"
              className="w-full"
            />
          </div>

          {/* Folder Copy */}
          {deliveryType === 1 && (
            <>
              <div>
                <label htmlFor="path" className="mb-1 block text-sm">
                  Path
                </label>
                <InputText
                  id="path"
                  {...register("path")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">{errors.path?.message}</div>
              </div>

              <div>
                <label htmlFor="subFolderName" className="mb-1 block text-sm">
                  Sub Folder Name
                </label>
                <InputText
                  id="subFolderName"
                  {...register("subFolderName")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.subFolderName?.message}
                </div>
              </div>
            </>
          )}

          {/* SFTP */}
          {deliveryType === 2 && (
            <>
              <div>
                <label htmlFor="url" className="mb-1 block text-sm">
                  URL
                </label>
                <InputText id="url" {...register("url")} className="w-full" />
                <div className="text-xs text-red-900">
                  {errors.url?.message}
                </div>
              </div>

              <div>
                <label htmlFor="port" className="mb-1 block text-sm">
                  Port
                </label>
                <InputText
                  id="port"
                  value={watch("port") || ""}
                  onChange={(e) =>
                    setValue("port", Number(e.target.value), {
                      shouldValidate: true,
                    })
                  }
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.port?.message}
                </div>
              </div>

              <div>
                <label htmlFor="userName" className="mb-1 block text-sm">
                  Username
                </label>
                <InputText
                  id="userName"
                  {...register("userName")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.userName?.message}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-1 block text-sm">
                  Password
                </label>
                <InputText
                  id="password"
                  {...register("password")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.password?.message}
                </div>
              </div>

              <div>
                <label htmlFor="privateKeyFile" className="mb-1 block text-sm">
                  Private Key File Path
                </label>
                <InputText
                  id="privateKeyFile"
                  {...register("privateKeyFile")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.privateKeyFile?.message}
                </div>
              </div>

              <div>
                <label htmlFor="passPhrase" className="mb-1 block text-sm">
                  Pass Phrase
                </label>
                <InputText
                  id="passPhrase"
                  {...register("passPhrase")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.passPhrase?.message}
                </div>
              </div>

              <div>
                <label htmlFor="path" className="mb-1 block text-sm">
                  Path
                </label>
                <InputText id="path" {...register("path")} className="w-full" />
                <div className="text-xs text-red-900">{errors.path?.message}</div>
              </div>

              <div>
                <label htmlFor="subFolderName" className="mb-1 block text-sm">
                  Sub Folder Name
                </label>
                <InputText
                  id="subFolderName"
                  {...register("subFolderName")}
                  className="w-full"
                />
                <div className="text-xs text-red-900">
                  {errors.subFolderName?.message}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-row-reverse">
          <Button
            type="submit"
            label="Save"
            icon="pi pi-check"
            loading={showLoading}
            disabled={showLoading}
            className="p-button-sm p-button-success p-button-outlined"
          />
          <div className="mx-2" />
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            onClick={onCancel}
            className="p-button-sm p-button-danger p-button-outlined"
          />
        </div>
      </form>
    </div>
  );
};

export default DeliveryConfigForm;
